import { type ChangeEvent } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IconCamera, IconTrash } from "@tabler/icons-react";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../../../domains/user/hooks/useCurrentUser";
import { saveAvatarPick } from "../../../../domains/user/saveAvatarPick";
import { compressAvatarImage } from "../../../../domains/user/utils/compressAvatarImage";
import { defaultAvatarImages } from "../../../../domains/user/utils/pickUserImage";
import {
  useAvatarPicker,
  setPickedAvatar,
  setRemovedAvatar,
  resetAvatarPicker,
} from "../../../../domains/user/stores/avatarPickerStore";
import { displayToast } from "../../../../components/app/AppToast";
import { getInitials } from "../../../../components/ProfilePicture";
import { Avatar } from "../../../../components/ui/Avatar";
import { Button } from "../../../../components/ui/Button";
import {
  ACCOUNT_PAGE,
  openDesktopSettings,
  leaveDesktopSettingsPage,
} from "../../../../domains/app/stores/desktopSettingsStore";
import { SettingsPage, SettingsSection } from "../components/SettingsPanel";

/**
 * Picking the profile photo: a page of the settings modal with its way back to
 * Account, never a dialog over the modal.
 *
 * The pick is staged in `avatarPickerStore` and written only by Save, so
 * "Remove photo" here is a choice the reader still confirms, not a deletion.
 */
export function AvatarSection(): React.JSX.Element {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const pick = useAvatarPicker();

  const picture = currentUser === null ? null : currentUser.profilePicture;
  const customUrl =
    picture !== null && picture.image !== null && picture.image.url !== ""
      ? picture.image.url
      : "";
  const defaultUrl =
    picture !== null && picture.name !== null
      ? defaultAvatarImages[picture.name].default
      : "";

  // The freshly-picked photo, the empty placeholder once it is dropped, else
  // whatever the account currently carries.
  const displayedUrl =
    pick.status === "picked"
      ? pick.dataUrl
      : pick.status === "removed"
        ? ""
        : customUrl !== ""
          ? customUrl
          : defaultUrl;

  const canRemove =
    pick.status === "picked" ||
    (pick.status === "untouched" && displayedUrl !== "");

  function handleFile(event: ChangeEvent<HTMLInputElement>): void {
    const files = event.target.files;
    if (files === null) return;
    const file = files.item(0);
    // Cleared before the async work: re-picking the same file must fire a new
    // change event.
    event.target.value = "";
    if (file === null) return;
    void compressAvatarImage(file).then(setPickedAvatar, () => {
      displayToast(t("Something went wrong. Please try again."), "error");
    });
  }

  // useForm only for a submit-in-flight flag, without owning `useState`: a
  // second click must not send a second upload or delete.
  const { handleSubmit, formState } = useForm();

  async function save(): Promise<void> {
    const result = await saveAvatarPick(pick);
    if (result === "untouched") {
      leaveDesktopSettingsPage({ page: "avatar" }, ACCOUNT_PAGE);
      return;
    }
    if (result === "failed") {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    await reloadCurrentUser();
    displayToast(t("Profile picture updated"), "success");
    resetAvatarPicker();
    leaveDesktopSettingsPage({ page: "avatar" }, ACCOUNT_PAGE);
  }

  function cancel(): void {
    // The staged pick must not survive the page: coming back to it would
    // otherwise show a removal the reader just backed out of.
    resetAvatarPicker();
    openDesktopSettings(ACCOUNT_PAGE);
  }

  return (
    <SettingsPage back={{ label: t("Account"), onBack: cancel }}>
      <SettingsSection title={t("Edit avatar")} description={null}>
        <div className="flex flex-col items-start gap-6 pt-1.5">
          {displayedUrl !== "" ? (
            <Avatar
              variant="image"
              src={displayedUrl}
              alt=""
              imageFit="cover"
              size="xl"
              badge={null}
              className="rounded-full"
            />
          ) : (
            <Avatar
              variant="initials"
              initials={getInitials(
                currentUser === null ? null : currentUser.fullname
              )}
              size="xl"
              badge={null}
              className="rounded-full"
            />
          )}

          <div className="flex flex-wrap items-center gap-3">
            {/* The file input covers the button rather than sitting next to it:
              a bare <input type="file"> cannot be styled, and a label-for
              pairing loses the keyboard focus ring the Button already has. */}
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={handleFile}
                disabled={formState.isSubmitting}
                aria-label={t("Upload profile picture")}
                className="absolute inset-0 size-full cursor-pointer opacity-0"
              />
              <Button
                type="button"
                variant="secondary"
                size="md"
                leftIcon={<IconCamera size={20} />}
              >
                {t("Upload profile picture")}
              </Button>
            </div>
            {canRemove ? (
              <Button
                type="button"
                variant="ghost"
                size="md"
                className="text-ds-strawberry-600"
                leftIcon={<IconTrash size={20} />}
                disabled={formState.isSubmitting}
                onClick={setRemovedAvatar}
              >
                {t("Remove photo")}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="flex gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-w-37"
            onClick={cancel}
          >
            {t("Cancel")}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="min-w-37"
            disabled={pick.status === "untouched" || formState.isSubmitting}
            onClick={(event) => {
              void handleSubmit(save)(event);
            }}
          >
            {t("Save")}
          </Button>
        </div>
      </SettingsSection>
    </SettingsPage>
  );
}
